import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useState } from 'react';
import { Authenticated, usePermissions } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  BillingExportDetailResponse,
  BillingExportListItem,
  BillingExportStatus,
} from '../../interfaces/billing.async';
import { IClinic } from '../../interfaces/clinic';
import { getClinicsForSelect } from '../../lib/clinic';
import { AuthService } from '../../services';
import { BillingService } from '../../services/billing';
import { Roles } from '../../util/constants';
import { SectionTitle } from '../common/SectionTitle';
import { BillingExportDetail } from './BillingExportDetail';

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error';

const getStatusColor = (status: string): ChipColor => {
  switch (status) {
    case BillingExportStatus.Pending:
      return 'default';
    case BillingExportStatus.Processing:
      return 'primary';
    case BillingExportStatus.Completed:
      return 'success';
    case BillingExportStatus.CompletedWithErrors:
      return 'warning';
    case BillingExportStatus.Failed:
      return 'error';
    default:
      return 'default';
  }
};

const formatStatus = (status: string): string => {
  switch (status) {
    case BillingExportStatus.Pending:
      return 'Pending';
    case BillingExportStatus.Processing:
      return 'Processing';
    case BillingExportStatus.Completed:
      return 'Completed';
    case BillingExportStatus.CompletedWithErrors:
      return 'With Errors';
    case BillingExportStatus.Failed:
      return 'Failed';
    default:
      return status;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Allowed clinic IDs from environment
const ALLOWED_CLINIC_IDS: number[] = JSON.parse(
  process.env.REACT_APP_DRCHRONO_ALLOWED_CLINIC_IDS || '[]',
);

export const BillingExportHistory: FC = () => {
  const { loaded } = usePermissions();
  const { requestId } = useParams<{ requestId?: string }>();

  const [exports, setExports] = useState<BillingExportListItem[]>([]);
  const [selectedExport, setSelectedExport] = useState<BillingExportDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clinics
  const [clinics, setClinics] = useState<IClinic[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(true);

  // Filters
  const [clinicId, setClinicId] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchExports = async () => {
    if (!clinicId) return;

    setIsLoading(true);
    setError(null);

    try {
      const billingService = new BillingService();
      const response = await billingService.listBillingExports(clinicId, {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(statusFilter && { status: statusFilter }),
      });

      if (response) {
        setExports(response.exports);
        setTotal(response.pagination.total);
      } else {
        setError('Failed to load exports');
      }
    } catch (err) {
      setError('Failed to load exports');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExportDetail = async (id: string) => {
    setIsLoadingDetail(true);

    try {
      const billingService = new BillingService();
      const response = await billingService.getBillingExportDetail(id);

      if (response) {
        setSelectedExport(response);
      }
    } catch (err) {
      console.error('Failed to load export detail:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Load clinics on mount
  useEffect(() => {
    const loadClinics = async () => {
      setClinicsLoading(true);
      try {
        const clinicsData = await getClinicsForSelect({ ids: ALLOWED_CLINIC_IDS });
        setClinics(clinicsData);
        // Set default clinic to first one
        if (clinicsData.length > 0) {
          setClinicId(clinicsData[0].id);
        }
      } catch (err) {
        console.error('Failed to load clinics:', err);
      } finally {
        setClinicsLoading(false);
      }
    };
    loadClinics();
  }, []);

  useEffect(() => {
    if (clinicId) {
      fetchExports();
    }
  }, [clinicId, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    if (requestId) {
      fetchExportDetail(requestId);
    } else {
      setSelectedExport(null);
    }
  }, [requestId]);

  const handleViewDetail = (id: string) => {
    fetchExportDetail(id);
  };

  const handleCloseDetail = () => {
    setSelectedExport(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!loaded) {
    return <Typography>Loading...</Typography>;
  }

  if (!AuthService.checkRoleAccess(Roles.Billing)) {
    return (
      <Card>
        <CardContent>
          <Container maxWidth='md'>
            <Grid container spacing={4} justifyContent='center' alignItems='center'>
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center' }}>
                  <LockIcon color='error' sx={{ fontSize: 48 }} />
                  <Typography>You do not have access to this page</Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </CardContent>
      </Card>
    );
  }

  // Show detail view if an export is selected
  if (selectedExport) {
    return (
      <Authenticated>
        <BillingExportDetail
          exportDetail={selectedExport}
          onClose={handleCloseDetail}
          isLoading={isLoadingDetail}
        />
      </Authenticated>
    );
  }

  return (
    <Authenticated>
      <Card>
        <CardContent>
          <SectionTitle label='Export History' variant='h5' style={{ fontWeight: '500' }} />
          <Divider sx={{ mb: 2 }} />

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl size='small' sx={{ minWidth: 250 }}>
              <InputLabel>Clinic</InputLabel>
              <Select
                value={clinicId}
                label='Clinic'
                onChange={(e) => setClinicId(Number(e.target.value))}
                disabled={clinicsLoading}>
                {clinicsLoading ? (
                  <MenuItem value={0}>Loading...</MenuItem>
                ) : (
                  clinics.map((clinic) => (
                    <MenuItem key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl size='small' sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label='Status'
                onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value=''>All</MenuItem>
                <MenuItem value={BillingExportStatus.Pending}>Pending</MenuItem>
                <MenuItem value={BillingExportStatus.Processing}>Processing</MenuItem>
                <MenuItem value={BillingExportStatus.Completed}>Completed</MenuItem>
                <MenuItem value={BillingExportStatus.CompletedWithErrors}>With Errors</MenuItem>
                <MenuItem value={BillingExportStatus.Failed}>Failed</MenuItem>
              </Select>
            </FormControl>

            <IconButton onClick={fetchExports} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Error */}
          {error && (
            <Typography color='error' sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Table */}
          <TableContainer component={Paper} variant='outlined'>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Total Rows</TableCell>
                  <TableCell>Processed</TableCell>
                  <TableCell>Errors</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell align='center'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align='center' sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : exports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align='center' sx={{ py: 4 }}>
                      <Typography color='text.secondary'>No exports found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  exports.map((exp) => (
                    <TableRow key={exp.requestId} hover>
                      <TableCell>{formatDate(exp.createdAt)}</TableCell>
                      <TableCell>{exp.totalRows}</TableCell>
                      <TableCell>{exp.processedRows}</TableCell>
                      <TableCell>
                        {exp.errorMessage ? (
                          <Typography variant='body2' color='error'>
                            {exp.errorMessage.substring(0, 30)}...
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{Math.round(exp.progress)}%</TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatus(exp.status)}
                          color={getStatusColor(exp.status)}
                          size='small'
                        />
                      </TableCell>
                      <TableCell>{exp.userEmail || '-'}</TableCell>
                      <TableCell align='center'>
                        <IconButton size='small' onClick={() => handleViewDetail(exp.requestId)}>
                          <VisibilityIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component='div'
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Authenticated>
  );
};

export default BillingExportHistory;
